import type { Principal } from '../../../types/auth';
import type {
    BodyChunk,
    HttpMethod, HttpResponse,
    MutableHttpHeaders,
    ReadonlyHeaderValue
} from '../../../types/web/http';
import { ServerHttpRequest, SslInfo } from '../../../types/web/server';
import { AbstractServerHttpResponse, DefaultWebExchange } from '../../server/exchange.ts';
import { MapHttpHeaders } from '../../http/headers.ts';
import codecConfig from '../../http/codec/config.ts';
import stream from 'node:stream/web';
import { AbstractServerHttpRequest } from '../../http/server/exchange.ts';

export class MockServerHttpRequest extends AbstractServerHttpRequest implements ServerHttpRequest {
    readonly #url: URL;
    readonly #sslInfo?: SslInfo;
    #body?: Blob;
    upgrade = false;

    constructor(url: URL | string, method?: HttpMethod) {
        super(new MapHttpHeaders());
        if (typeof url === 'string') {
            if (URL.canParse(url)) {
                url = new URL(url);
            }
            else if (URL.canParse(url, 'http://localhost')) {
                url = new URL(url, 'http://localhost');
            }
            else {
                throw new TypeError('URL cannot parse url');
            }
        }
        this.#url = url;
        this.method = method ?? 'GET';
        this.setHeader('Host', this.#url.hostname);
        this.path = this.#url.pathname ?? '/';
    }

    static get(url: URL | string) {
        return new MockServerHttpRequest(url, 'GET');
    }

    static post(url: URL | string) {
        return new MockServerHttpRequest(url, 'POST');
    }

    static options(url: URL | string) {
        return new MockServerHttpRequest(url, 'OPTIONS');
    }

    protected initSslInfo(): SslInfo | undefined {
        return this.#sslInfo;
    }

    getNativeRequest<T>(): T {
        throw new Error(`This is a mock. No running server, no native request available.`);
    }

    method: HttpMethod;
    path: string;

    get host() {
        return super.parseHost(this.#url.host);
    }

    get protocol() {
        return super.parseProtocol((this.#url.protocol).slice(0, -1));
    }

    get body() {
        if (this.#body !== undefined) {
            const blob = this.#body;
            const asyncIterator: AsyncGenerator<BodyChunk> = (async function* () {
                const bytes = await blob.bytes();
                yield bytes;
            })();
            return stream.ReadableStream.from(asyncIterator) as ReadableStream<BodyChunk>;
        }
    }

    blob(): Promise<Blob> {
        const body = this.#body;
        return body ? Promise.resolve(body) : Promise.reject(new Error(`no body set`));
    }

    async text() {
        const blob = await this.blob();
        return await blob.text();
    }

    async formData() {
        const blob = await this.blob();
        const text = await blob.text();
        return new URLSearchParams(text);
    }

    async json() {
        const blob = await this.blob();
        if (blob.size === 0) {
            return undefined;
        }
        const text = await blob.text();
        return JSON.parse(text);
    }

    async writeBody(body?: ReadableStream<BodyChunk> | Promise<BodyChunk> | BodyChunk) {
        if (body === undefined) {
            this.#body = new Blob([]);
            return;
        }
        if (body instanceof ReadableStream) {
            const chunks: BodyChunk[] = [];
            const reader = body.getReader();
            let done = false;
            while (!done) {
                const { value, done: readDone } = await reader.read();
                if (readDone) {
                    done = true;
                }
                else {
                    chunks.push(value);
                }
            }
            this.#body = new Blob(chunks);
        }
        else {
            body = await body;
            if (typeof body === 'string') {
                this.#body = new Blob([body], { type: 'text/plain' });
            }
            else {
                this.#body = new Blob([body]);
            }
        }
        if (!this.headers.has('content-type')) {
            this.setHeader('Content-Type', this.#body.type || 'application/octet-stream');
        }

    }

    get URL() {
        return new URL(this.path + this.#url.search + this.#url.hash, `${this.protocol}://${this.host}`);
    }

    addHeader(name: string, value: string | (readonly string[])): this {
        (this.headers as MutableHttpHeaders).add(name, value);
        return this;
    }

    setHeader(name: string, value: ReadonlyHeaderValue): this {
        (this.headers as MutableHttpHeaders).set(name, value);
        return this;
    }
}

export class MockServerHttpResponse extends AbstractServerHttpResponse implements HttpResponse<MutableHttpHeaders> {
    #writeHandler: (body: ReadableStream<BodyChunk>) => Promise<boolean>;
    #body: () => ReadableStream<BodyChunk> = () => {
        throw new Error('No content was written to the response body nor end was called on this response.');
    };

    constructor() {
        super(new MapHttpHeaders());
        this.#writeHandler = async (body) => {
            const chunks: BodyChunk[] = [];
            let bodyStream: ReadableStream<BodyChunk> | undefined;
            this.#body = () => {
                bodyStream ??= stream.ReadableStream.from(chunks) as ReadableStream<BodyChunk>;
                return bodyStream;
            };

            const reader = body.getReader();
            let done = false;
            do {
                const { value, done: readDone } = await reader.read();
                if (readDone) {
                    done = true;
                }
                else {
                    chunks.push(value);
                }
            }
            while (!done);
            return true;
        }
    }

    get statusCode() {
        return super.statusCode!; // :(
    }

    set writeHandler(handler: (body: ReadableStream<BodyChunk>) => Promise<boolean>) {
        this.#body = () => {
            throw new Error('Not available with custom write handler');
        }
        this.#writeHandler = handler;
    }


    getNativeResponse<T>(): T {
        throw new Error('This is a mock. No running server, no native response.');
    }

    protected applyStatusCode(): void {
    }

    protected applyHeaders(): void {
    }

    protected applyCookies(): void {
        for (const cookie of this.cookies) {
            this.headers.add('Set-Cookie', super.setCookieValue(cookie));
        }
    }

    bodyInternal(body: Promise<BodyChunk | void>): Promise<boolean> {
        const it = (async function* () {
            const resolved = await body;
            if (resolved === undefined) {
                return;
            }
            yield resolved;
        })();
        return this.#writeHandler(stream.ReadableStream.from(it) as ReadableStream<BodyChunk>);
    }

    async end(): Promise<boolean> {
        return this.doCommit(async () => {
            return await this.#writeHandler(stream.ReadableStream.from([]) as ReadableStream);
        });
    }

    getBody() {
        return this.#body();
    }
}


export class MockWebExchange extends DefaultWebExchange<MockServerHttpRequest, MockServerHttpResponse> {
    readonly #principal?: Principal;

    constructor(request: MockServerHttpRequest, response?: MockServerHttpResponse, principal?: Principal) {
        super(request, response ?? new MockServerHttpResponse(), codecConfig.serverConfigurer.create());
        this.#principal = principal;
    }

    static from (request: MockServerHttpRequest): MockWebExchange {
        return new MockWebExchange(request);
    }

    principal<P extends Principal>(): Promise<P | undefined> {
        return Promise.resolve(this.#principal as P);
    }
}
